/*
 * АВТОПРОВЕРКА ЗАДАНИЯ — сравнение детали ученика с критериями урока.
 *
 * ГЛАВНОЕ РЕШЕНИЕ: проверяем РЕЗУЛЬТАТ, а не способ. Ребёнок может прийти
 * к нужной детали своим путём — выдавить прямоугольник или повернуть профиль,
 * сделать отверстие вырезом или булевой операцией. Всё это правильно.
 * Поэтому мы не смотрим на список операций, а меряем готовое тело:
 * габариты, объём, площадь, число тел и отверстий.
 *
 * ПОЧЕМУ НЕ СРАВНЕНИЕ С ЭТАЛОННЫМ ФАЙЛОМ. Побитовое сравнение геометрии
 * ядром OCCT — дорого (десятки секунд) и хрупко: та же деталь, построенная
 * иначе, даст другие рёбра и другой порядок граней. Набор числовых критериев
 * с допусками покрывает всё, что нужно проверить в учебной задаче,
 * и считается мгновенно из уже существующего MEASURE_BODY.
 *
 * ВТОРАЯ ФУНКЦИЯ ПРОВЕРКИ — ПОДСКАЗКА. Ответ «неверно» ничему не учит.
 * Каждый критерий, кроме порога, знает, что сказать при расхождении
 * и в какую сторону оно ушло: «Деталь шире, чем нужно: 64 вместо 60 —
 * проверь размер первого эскиза».
 */

/** Допуск по умолчанию для линейных размеров, мм. */
const DEFAULT_LINEAR_TOL = 0.5;
/** Допуск по умолчанию для объёма и площади, доля. */
const DEFAULT_RATIO_TOL = 0.05;

/** Округление для показа: не длиннее двух знаков, без хвоста «.00». */
function fmt(value, digits = 2) {
    if (value == null || Number.isNaN(value)) return '—';
    const s = Number(value).toFixed(digits);
    /*
     * 🔴 ХВОСТ РЕЖЕМ ТОЛЬКО ПОСЛЕ ТОЧКИ.
     *
     * Раньше стояло s.replace(/\.?0+$/, ''), и у ЦЕЛОГО числа регулярка
     * отгрызала значащие нули: 31200 → «312», 36000 → «36», 100 → «1».
     * В уроке 3 из-за этого ожидаемый объём 31200 мм³ показывался
     * как «нужно 312 мм³» — ребёнок видел заведомо неверную цель.
     *
     * Дробную часть по-прежнему подчищаем: 12.50 → «12.5», 8.00 → «8».
     */
    return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
}

/** Габариты тела, отсортированные по возрастанию.
 *  Сортировка нужна, чтобы деталь засчиталась независимо от того,
 *  на какой плоскости ребёнок её начал: брусок 60×40×25 остаётся
 *  тем же бруском, лёжа он или стоя. */
function sortedSize(bbox) {
    if (!bbox || !bbox.size) return null;
    return [...bbox.size].map(Number).sort((a, b) => a - b);
}

/*
 * ── ПРОВЕРЯЮЩИЕ ПО ВИДАМ КРИТЕРИЕВ ──────────────────────────────────────
 *
 * Каждый получает (criterion, measured) и возвращает
 * { ok, actual, expected, message }. measured — то, что намерил редактор:
 * { bodies: [{ id, volume, area, bbox, com }], holeCount, ... }.
 */

const CHECKERS = {
    /* Габариты — три числа в любом порядке (см. sortedSize). */
    size({ value, tolerance = DEFAULT_LINEAR_TOL, hint }, measured) {
        const body = measured.bodies[0];
        const actual = body && sortedSize(body.bbox);
        if (!actual) {
            return { ok: false, actual: '—', expected: value.join('×'),
                message: 'Детали в сцене нет — построй тело.' };
        }
        const expected = [...value].map(Number).sort((a, b) => a - b);
        // Ищем самое большое расхождение — о нём и говорим.
        let worstIdx = 0, worstDiff = 0;
        expected.forEach((exp, i) => {
            const d = Math.abs(actual[i] - exp);
            if (d > worstDiff) { worstDiff = d; worstIdx = i; }
        });
        const ok = worstDiff <= tolerance;
        const actualStr = actual.map((v) => fmt(v, 1)).join(' × ');
        const expectedStr = expected.map((v) => fmt(v, 1)).join(' × ');
        let message = null;
        if (!ok) {
            const a = actual[worstIdx], e = expected[worstIdx];
            const dir = a > e ? 'больше' : 'меньше';
            message = hint || `Один из размеров ${dir}, чем нужно: `
                + `${fmt(a, 1)} вместо ${fmt(e, 1)} мм. `
                + 'Проверь размеры в эскизе и высоту выдавливания.';
        }
        return { ok, actual: actualStr + ' мм', expected: expectedStr + ' мм', message };
    },

    /* Объём — с относительным допуском: он ловит лишние или забытые вырезы. */
    volume({ value, tolerance = DEFAULT_RATIO_TOL, hint }, measured) {
        const body = measured.bodies[0];
        const actual = body ? Number(body.volume) : null;
        if (actual == null || Number.isNaN(actual)) {
            return { ok: false, actual: '—', expected: fmt(value, 0),
                message: 'Детали в сцене нет — построй тело.' };
        }
        const expected = Number(value);
        const diff = Math.abs(actual - expected) / expected;
        const ok = diff <= tolerance;
        let message = null;
        if (!ok) {
            message = hint || (actual > expected
                ? 'Материала больше, чем должно быть: похоже, какой-то вырез '
                  + 'не сделан или сделан меньше нужного.'
                : 'Материала меньше, чем должно быть: похоже, лишний вырез '
                  + 'или деталь получилась тоньше.');
        }
        return {
            ok,
            actual: fmt(actual, 0) + ' мм³',
            expected: fmt(expected, 0) + ' мм³ ±' + fmt(tolerance * 100, 0) + '%',
            message,
        };
    },

    /* Число отдельных тел — «должна получиться одна деталь», «крышка и коробка». */
    bodyCount({ value, hint }, measured) {
        const actual = measured.bodies.length;
        const expected = Number(value);
        const ok = actual === expected;
        let message = null;
        if (!ok) {
            message = hint || (actual > expected
                ? `Тел получилось ${actual}, а нужно ${expected}. `
                  + 'Проверь операции: где-то вместо «Объединить» стоит «Новое тело».'
                : `Тел ${actual}, а нужно ${expected}. Не все детали построены.`);
        }
        return { ok, actual: String(actual), expected: String(expected), message };
    },

    /* Отверстия — считаем по цилиндрическим граням (см. countHoles ниже). */
    holes({ value, hint }, measured) {
        const actual = measured.holeCount;
        const expected = Number(value);
        const ok = actual === expected;
        let message = null;
        if (!ok) {
            message = hint || (actual < expected
                ? `Отверстий найдено ${actual}, а нужно ${expected}. `
                  + 'Не забыл сделать вырез?'
                : `Отверстий ${actual}, а нужно ${expected}. Лишние — убери.`);
        }
        return { ok, actual: String(actual), expected: String(expected), message };
    },

    /* Площадь поверхности — косвенно ловит забытые скругления и фаски:
     * острый угол и скруглённый дают заметно разную площадь. */
    area({ value, tolerance = DEFAULT_RATIO_TOL, hint }, measured) {
        const body = measured.bodies[0];
        const actual = body ? Number(body.area) : null;
        if (actual == null || Number.isNaN(actual)) {
            return { ok: false, actual: '—', expected: fmt(value, 0),
                message: 'Детали в сцене нет — построй тело.' };
        }
        const expected = Number(value);
        const ok = Math.abs(actual - expected) / expected <= tolerance;
        return {
            ok,
            actual: fmt(actual, 0) + ' мм²',
            expected: fmt(expected, 0) + ' мм²',
            message: ok ? null : (hint
                || 'Площадь поверхности не сходится. Обычно так бывает, когда '
                   + 'забыты скругления или фаски.'),
        };
    },

    /* Есть ли в документе операция такого вида. Использовать редко:
     * это проверка способа, а не результата. Оправдана там, где сам приём —
     * и есть цель урока («сделай оболочку», «поставь скругление»). */
    feature({ value, hint }, measured) {
        const kinds = Array.isArray(value) ? value : [value];
        const found = kinds.filter((k) => measured.featureKinds.includes(k));
        const ok = found.length === kinds.length;
        /* Имена — по РЕАЛЬНЫМ feature.kind из rebuildPipeline.js.
           Раньше здесь был несуществующий 'pattern': критерий с ним
           не сработал бы никогда, а в сообщении показал бы сырой ключ. */
        const names = { extrude: 'выдавливание', revolve: 'вращение',
            fillet: 'скругление', chamfer: 'фаска', shell: 'оболочка',
            hole: 'отверстие', sweep: 'сдвиг по траектории', loft: 'лофт',
            circPattern: 'круговой массив', rectPattern: 'прямоугольный массив',
            mirror: 'зеркало', coil: 'спираль', text: 'текст',
            combine: 'объединение', scale: 'масштаб', moveCopy: 'перемещение' };
        const missing = kinds.filter((k) => !measured.featureKinds.includes(k))
            .map((k) => names[k] || k);
        return {
            ok,
            actual: ok ? 'есть' : 'нет',
            expected: kinds.map((k) => names[k] || k).join(', '),
            message: ok ? null : (hint
                || `В работе не хватает операции: ${missing.join(', ')}.`),
        };
    },

    /* Зазор между двумя телами: посадка одной детали на другую.
     * Нужен там, где деталь должна надеваться — коробка с крышкой,
     * вал во втулке. Считаем по габаритным рамкам, этого хватает
     * для учебных посадок.
     *
     * 🔴 Сравниваем ДВА НАИБОЛЬШИХ размера — сечение, которым детали и
     * входят друг в друга. Раньше брались наименьшие, и у коробки
     * 60x40x30 с крышкой 60.3x40.3x12 проверка сравнивала высоту крышки
     * (12) с высотой коробки (30) и выдавала «зазор 9 мм» на верно
     * собранной работе. Высота надевающейся детали к посадке отношения
     * не имеет: крышка мельче коробки по высоте — это норма. */
    gap({ min = 0.1, max = 0.6, hint }, measured) {
        if (measured.bodies.length < 2) {
            return { ok: false, actual: '—', expected: `${fmt(min)}…${fmt(max)} мм`,
                message: 'Для проверки зазора нужны две детали.' };
        }
        const [a, b] = measured.bodies;
        const sa = sortedSize(a.bbox), sb = sortedSize(b.bbox);
        if (!sa || !sb) {
            return { ok: false, actual: '—', expected: `${fmt(min)}…${fmt(max)} мм`,
                message: 'Не удалось измерить детали.' };
        }
        /*
         * Зазор на сторону — по тому размеру сечения, где детали сидят
         * плотнее всего. Берём наименьший из двух зазоров: он и решает,
         * наденется деталь или заклинит.
         */
        const gapLong = Math.abs(sa[2] - sb[2]) / 2;
        const gapWide = Math.abs(sa[1] - sb[1]) / 2;
        const actual = Math.min(gapLong, gapWide);
        const ok = actual >= min && actual <= max;
        let message = null;
        if (!ok) {
            message = hint || (actual < min
                ? `Зазор ${fmt(actual)} мм — слишком мало, детали не соберутся. `
                  + `Нужно от ${fmt(min)} мм.`
                : `Зазор ${fmt(actual)} мм — слишком много, будет болтаться. `
                  + `Нужно не больше ${fmt(max)} мм.`);
        }
        return { ok, actual: fmt(actual) + ' мм',
            expected: `${fmt(min)}…${fmt(max)} мм`, message };
    },
};

/*
 * ПОДСЧЁТ ОТВЕРСТИЙ.
 *
 * Точного «числа отверстий» ядро не даёт — в терминах B-Rep отверстия нет,
 * есть цилиндрические грани. Считаем их и делим пополам: сквозное отверстие
 * в детали обычно даёт одну грань, но у скруглённых краёв и глухих отверстий
 * граней бывает больше. Поэтому берём число ЗАМКНУТЫХ цилиндрических граней
 * — эту величину отдаёт воркер вместе с измерением.
 *
 * Если воркер не прислал holeCount (старая сборка) — критерий «отверстия»
 * не проверяем, а честно говорим, что не смогли, вместо ложного «неверно».
 */

/**
 * Проверить работу ученика по критериям урока.
 *
 * @param {Array} criteria — список критериев из описания урока:
 *        [{ kind:'size', value:[60,40,25], tolerance:0.5, label:'Габариты' }, …]
 * @param {Object} measured — измерения сцены:
 *        { bodies:[{id,volume,area,bbox,com}], holeCount, featureKinds:[] }
 * @returns {{ passed:boolean, results:Array, firstProblem:string|null }}
 */
export function checkCriteria(criteria, measured) {
    const safe = {
        bodies: measured?.bodies || [],
        holeCount: measured?.holeCount ?? null,
        featureKinds: measured?.featureKinds || [],
    };

    const results = (criteria || []).map((criterion) => {
        const checker = CHECKERS[criterion.kind];
        if (!checker) {
            return { ...criterion, ok: false, skipped: true,
                actual: '—', expected: '—',
                message: `Неизвестный критерий «${criterion.kind}».` };
        }
        // Отверстия старая сборка воркера не считает — не заваливаем работу.
        if (criterion.kind === 'holes' && safe.holeCount == null) {
            return { ...criterion, ok: true, skipped: true,
                actual: '—', expected: String(criterion.value),
                message: 'Отверстия проверить не удалось — засчитано.' };
        }
        const outcome = checker(criterion, safe);
        return { ...criterion, ...outcome };
    });

    const failed = results.filter((r) => !r.ok);
    return {
        passed: failed.length === 0,
        results,
        firstProblem: failed.length ? failed[0].message : null,
    };
}

/** Человеческое имя критерия для панели — если урок не задал своё. */
export function criterionLabel(criterion) {
    if (criterion.label) return criterion.label;
    return {
        size: 'Габариты',
        volume: 'Объём',
        area: 'Площадь поверхности',
        bodyCount: 'Количество деталей',
        holes: 'Отверстия',
        feature: 'Нужные операции',
        gap: 'Зазор',
    }[criterion.kind] || criterion.kind;
}
